import { useEffect, useRef } from 'react';
import socket from 'shared/socket';
import { getSchemaName } from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import { useNotificaciones } from 'shared/context/NotificacionesContext';

const MAX_IDS = 300;
const MAX_WINDOW_MS = 60000;
const DEDUPE_PEDIDO_MS = 10000;

// Estados que representan "un pedido entra a una cola" y solo deben notificarse
// UNA vez por pedido (sin importar cuantos ecos de sync-cart lleguen).
const ENTRANTES_DE_UNA_VEZ = ['panel', 'revision', 'verificacion'];

// Roles atados a una sede: solo deben recibir notificaciones de su propia sede.
const ROLES_POR_SEDE = ['delivery', 'armador', 'cocinero', 'mesero', 'cajero'];

// Convierte el payload de 'pedido_actualizado' en la clave del evento
// (trabajo entrante con area -> badge de tab, o cierre del ciclo informativo).
export const areaDePedido = (data) => {
  const estado = data && data.estado;
  if (!estado) return null;
  const esTienda = data.tipo === 'tienda' || data.tipo_pedido === 'tienda';

  if (esTienda) {
    if (estado === 'creado') return data.en_espera ? 'espera' : 'salon';
    if (estado === 'comanda_enviada' || estado === 'armado_cocina') return 'cocina';
    if (estado === 'armado') return 'armado';
    if (estado === 'cobrado') return 'cobrado';
    if (estado === 'anulado') return 'anulado';
    if (estado === 'servido') return 'servido';
    return null;
  }

  if (estado === 'pendiente') return 'panel';
  if (estado === 'en_revision') return 'revision';
  if (estado === 'pago_en_verificacion') return 'verificacion';
  if (estado === 'pagado') return 'armado';
  if (estado === 'armado_cocina') return 'cocina';
  // "listo" solo notifica a delivery si el pedido es online y de entrega.
  // Retiro/encomienda/tienda no deben avisar al delivery.
  if (estado === 'listo') {
    const te = data.tipo_entrega;
    if (te === 'retiro' || te === 'encomienda') return null;
    return 'delivery';
  }
  if (estado === 'en_camino') return 'en_camino';
  if (estado === 'llegada') return 'llegada';
  if (estado === 'entregado') return 'entregado';
  if (estado === 'despachado') return 'despachado';
  if (estado === 'rechazado') return 'rechazado';
  if (estado === 'cancelado') return 'cancelado';
  if (estado === 'credito_pagado') return 'credito_pagado';
  return null;
};

// Suprime la notificacion al propio actor de un evento de tienda (ya vio su toast local).
// EXCEPCION: el envio a cocina (comanda_enviada) SI notifica al actor, porque si el
// mismo usuario es mesero y cocinero (o admin probando), necesita escuchar que el
// pedido acaba de entrar a cocina.
export const esNotificacionPropia = (data, cedula) => {
  if (!cedula || !data) return false;
  if (data.tipo !== 'tienda') return false;
  if (data.estado === 'comanda_enviada') return false;
  const actor = data.actor_cedula;
  if (actor == null) return false;
  return String(actor) === String(cedula);
};

// Para roles atados a sede, filtra eventos de otras sedes (si el payload trae sede).
export const esSedePropia = (data, usuario) => {
  if (!usuario) return true;
  const rol = usuario.rol;
  if (!rol || ROLES_POR_SEDE.indexOf(rol) === -1) return true;
  const miSede = usuario.codigo_sede;
  if (!miSede) return true;
  const sede = data && (data.sede_codigo || data.codigo_sede);
  if (!sede) return true; // sin info de sede: no filtrar (evita falsos negativos)
  return String(sede) === String(miSede);
};

// Resuelve la clave de evento y el origen del sonido para un `pedido_actualizado`.
// `salio_de_cocina` (marcado por el backend al completar la cocina de un pedido de
// tienda) fuerza una clave/origen propios, para que el panel de armado reciba un
// aviso con sonido distinto al resto.
export const claveYOrigen = (data) => {
  const esTienda = data.tipo === 'tienda' || data.tipo_pedido === 'tienda';
  let origen = esTienda ? 'tienda' : 'online';
  let clave = areaDePedido(data);
  if (data.salio_de_cocina === true) {
    clave = 'tienda_armado';
    origen = 'cocina_tienda';
  }
  return { clave, origen };
};

// Configuracion de cada evento. `area` null = informativo (sin badge de tab).
// `voz` = frase que se anuncia por voz (speechSynthesis) en lugar del tono.
const EVENTO_CONF = {
  panel: { permiso: 'chat.acceder', titulo: 'Nuevo pedido', severidad: 'normal', tipo: 'pedido', page: 'chats', area: 'panel', voz: 'Nuevo pedido' },
  revision: { permiso: 'chat.acceder', titulo: 'Pedido en revision', severidad: 'normal', tipo: 'pedido', page: 'chats', area: 'revision', voz: 'Pedido en revisión' },
  verificacion: { permiso: 'chat.acceder', titulo: 'Comprobante por verificar', severidad: 'normal', tipo: 'pedido', page: 'chats', area: 'verificacion', voz: 'Comprobante por verificar' },
  cocina: { permiso: 'pedidos.cocinero', titulo: 'Comanda a cocina', severidad: 'critica', tipo: 'pedido', page: 'chats', area: 'cocina', voz: 'Nuevo pedido en cocina' },
  armado: { permiso: 'pedidos.armado', titulo: 'Pedido por armar', severidad: 'critica', tipo: 'armado', page: 'chats', area: 'armado', voz: 'Pedido listo para entregar' },
  // Pedido de tienda que acaba de salir de cocina: aviso propio al panel de armado
  // con una frase distinta a todas las demas.
  tienda_armado: { permiso: 'pedidos.armado', titulo: 'Pedido de tienda listo para armar', severidad: 'critica', tipo: 'armado', page: 'chats', area: 'armado', voz: 'Pedido listo para armar' },
  delivery: { permiso: 'pedidos.delivery', titulo: 'Pedido listo para delivery', severidad: 'critica', tipo: 'pedido', page: 'chats', area: 'delivery', voz: 'Pedido listo para llevar' },
  salon: { permiso: 'pedidos_tienda.salon_ver', titulo: 'Nuevo pedido en salon', severidad: 'normal', tipo: 'pedido', page: 'pedidos-tienda', area: 'salon', voz: 'Nuevo pedido en salón' },
  espera: { permiso: 'pedidos_tienda.espera_ver', titulo: 'Pedido en espera', severidad: 'normal', tipo: 'pedido', page: 'pedidos-tienda', area: 'espera', voz: 'Pedido en espera' },
  en_camino: { permiso: 'chat.acceder', titulo: 'Pedido en camino', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'Pedido en camino' },
  llegada: { permiso: 'chat.acceder', titulo: 'Delivery llego', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'El delivery llegó' },
  entregado: { permiso: 'chat.acceder', titulo: 'Pedido entregado', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'Pedido entregado' },
  despachado: { permiso: 'chat.acceder', titulo: 'Pedido despachado', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'Pedido despachado' },
  rechazado: { permiso: 'chat.acceder', titulo: 'Pedido rechazado', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'Pedido rechazado' },
  cancelado: { permiso: 'chat.acceder', titulo: 'Pedido cancelado', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'Pedido cancelado' },
  credito_pagado: { permiso: 'chat.acceder', titulo: 'Credito saldado', severidad: 'normal', tipo: 'pedido', page: 'chats', area: null, voz: 'Crédito saldado' },
  cobrado: { permiso: 'pedidos_tienda.pedido_cobrar', titulo: 'Pedido cobrado', severidad: 'normal', tipo: 'pedido', page: 'pedidos-tienda', area: null, voz: 'Pedido cobrado' },
  anulado: { permiso: 'pedidos_tienda.pedido_ver', titulo: 'Pedido anulado', severidad: 'normal', tipo: 'pedido', page: 'pedidos-tienda', area: null, voz: 'Pedido anulado' },
  servido: { permiso: 'pedidos_tienda.pedido_ver', titulo: 'Pedido servido', severidad: 'normal', tipo: 'pedido', page: 'pedidos-tienda', area: null, voz: 'Pedido servido' },
};

const useNotificacionesSocket = () => {
  const { tienePermiso, usuario } = useAuth();
  const { notificar, sonar } = useNotificaciones();
  const tpRef = useRef(tienePermiso);
  tpRef.current = tienePermiso;
  const usuarioRef = useRef(usuario);
  usuarioRef.current = usuario;
  const msgIdsRef = useRef(new Set());
  const ventanaRef = useRef(new Map());
  // Estados "entrantes" que solo ocurren UNA vez por pedido (un pedido entra a la
  // cola una sola vez). Dedupe permanente: evita que el eco/loop de sync-cart
  // reproduzca el sonido de "Nuevo pedido" (panel) cada ~10s en pedidos 'pendiente'.
  const entrantesVistosRef = useRef(new Set());

  useEffect(() => {
    // Dedupe con ventana temporal (clave -> timestamp). Se poda periodicamente.
    const esReciente = (key, ms) => {
      const t = Date.now();
      const prev = ventanaRef.current.get(key);
      if (prev && t - prev < ms) return true;
      ventanaRef.current.set(key, t);
      if (ventanaRef.current.size > 200) {
        for (const [k, v] of ventanaRef.current) {
          if (t - v > MAX_WINDOW_MS) ventanaRef.current.delete(k);
        }
      }
      return false;
    };

    const mismaEmpresa = (data) => {
      const u = usuarioRef.current;
      if (!u) return true;
      const eid = data && data.empresa_id;
      if (eid != null && u.idempresa != null && String(eid) !== String(u.idempresa)) return false;
      const sch = data && (data.schema || data.schema_name);
      if (sch) {
        const actual = getSchemaName();
        if (actual && sch !== actual) return false;
      }
      return true;
    };

    const handlePedido = (data) => {
      if (!data || !mismaEmpresa(data)) return;
      // Progreso de checklist (tachar un item) NO debe re-notificar: el backend
      // marca `notificar: false` cuando no hubo transicion de estado.
      if (data.notificar === false) return;
      const { clave, origen } = claveYOrigen(data);
      if (!clave) return;
      const conf = EVENTO_CONF[clave];
      if (!conf || !tpRef.current(conf.permiso)) return;
      const pid = data.pedido_id;
      if (pid == null) return;
      const u = usuarioRef.current;
      if (esNotificacionPropia(data, u?.cedula)) return;
      if (!esSedePropia(data, u)) return;
      if (ENTRANTES_DE_UNA_VEZ.includes(clave)) {
        const key = `${clave}:${pid}`;
        if (entrantesVistosRef.current.has(key)) return;
        entrantesVistosRef.current.add(key);
        // Evicción FIFO (no .clear()): si tiramos todo el set, los pedidos aun
        // pendientes se re-notificarían en su próximo eco (dedupe roto).
        if (entrantesVistosRef.current.size > MAX_IDS) {
          const oldest = entrantesVistosRef.current.values().next().value;
          entrantesVistosRef.current.delete(oldest);
        }
      } else if (esReciente(`${clave}:${pid}`, DEDUPE_PEDIDO_MS)) {
        return;
      }
      notificar({
        titulo: conf.titulo,
        mensaje: `Pedido #${pid}`,
        tipo: conf.tipo,
        severidad: conf.severidad,
        pedidoId: pid,
        area: conf.area || undefined,
        page: conf.page,
        voz: conf.voz,
        origen,
      });
    };

    const handleAsesorSolicitado = (data) => {
      if (!data || !mismaEmpresa(data) || !tpRef.current('chat.acceder')) return;
      if (esReciente(`asesor:${data.idcliente}`, 15000)) return;
      const nombre = data.nombre_cliente || `#${data.idcliente || '?'}`;
      notificar({
        titulo: 'Solicitud de asesor',
        mensaje: `${nombre} solicita un asesor`,
        tipo: 'mensaje',
        severidad: 'normal',
        page: 'chats',
      });
    };

    const handleNuevoMensaje = (data) => {
      if (!data || data.autor !== 'cliente' || !mismaEmpresa(data)) return;
      if (!tpRef.current('chat.acceder')) return;
      const mid = data.id;
      if (mid != null) {
        if (msgIdsRef.current.has(mid)) return;
        msgIdsRef.current.add(mid);
        if (msgIdsRef.current.size > MAX_IDS) {
          const oldest = msgIdsRef.current.values().next().value;
          msgIdsRef.current.delete(oldest);
        }
      }
      sonar('normal');
    };

    const handleStockBajo = (data) => {
      if (!data || !tpRef.current('inventario.ver')) return;
      let producto = data.producto;
      if (!producto && Array.isArray(data.alertas) && data.alertas.length) {
        producto = data.alertas[0].nombre || data.alertas[0].codigo || '';
      }
      if (esReciente(`stock:${data.sede || ''}:${producto}`, 30000)) return;
      notificar({
        titulo: 'Stock bajo',
        mensaje: producto ? `${producto} tiene stock bajo` : 'Hay productos con stock bajo',
        tipo: 'pedido',
        severidad: 'normal',
        page: 'inventario',
      });
    };

    const handleSolicitudResuelta = (data) => {
      if (!data || !mismaEmpresa(data) || !tpRef.current('clientes.ver')) return;
      notificar({
        titulo: 'Solicitud resuelta',
        mensaje: 'Una solicitud de registro fue atendida',
        tipo: 'fase',
        severidad: 'normal',
        page: 'clientes',
      });
    };

    const handleNuevoAviso = (data) => {
      if (!data || !data.titulo) return;
      if (!mismaEmpresa(data)) return;
      notificar({
        titulo: data.titulo,
        mensaje: data.mensaje || '',
        tipo: 'aviso',
        severidad: 'normal',
        voz: data.titulo,
      });
    };

    socket.on('pedido_actualizado', handlePedido);
    socket.on('asesor_solicitado', handleAsesorSolicitado);
    socket.on('nuevo_mensaje', handleNuevoMensaje);
    socket.on('alerta_stock_bajo', handleStockBajo);
    socket.on('solicitud_resuelta', handleSolicitudResuelta);
    socket.on('nuevo_aviso', handleNuevoAviso);

    return () => {
      socket.off('pedido_actualizado', handlePedido);
      socket.off('asesor_solicitado', handleAsesorSolicitado);
      socket.off('nuevo_mensaje', handleNuevoMensaje);
      socket.off('alerta_stock_bajo', handleStockBajo);
      socket.off('solicitud_resuelta', handleSolicitudResuelta);
      socket.off('nuevo_aviso', handleNuevoAviso);
    };
    // NOTA: `usuario`/`tienePermiso` NO van en las deps: los handlers los leen via
    // `usuarioRef`/`tpRef` (refs estables), y `notificar`/`sonar` son useCallback
    // estables. Incluir `usuario` aqui re-registraba los 5 handlers en cada
    // refrescarPermisos (churn innecesario). El efecto corre una sola vez.
  }, [notificar, sonar]);
};

export default useNotificacionesSocket;
